import {
  Heading,
  color,
  OnyxGray,
  BrandPurple,
  spaceToPx,
  Lighter3,
  getFontWeight,
  getFontSize,
  gradient,
  AquaTeal,
  radius,
  White,
} from 'canoe';
import { connectWithHammock, ENDPOINTS } from 'hammock';
import React, { useState, ChangeEvent } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Input } from 'styleguide';

import { getCategoryName, HIDDEN_TEMPLATES_CATEGORY } from 'commons/templates';
import TemplatePreview from 'craft/components/TemplatesTab/TemplatePreview';
import { setPaletteTab } from 'craft/state';
import { SearchIcon } from 'styleguide/icons';

import { TemplateCard } from './TemplateCard';
import { TemplateMetadata } from './types';

const FiltersContainer = styled.div`
  min-width: 290px;
  padding: ${spaceToPx('normal')}px 0 ${spaceToPx('normal')}px ${spaceToPx('l')}px;
  width: 290px;
  height: 100vh;
`;

const TemplatesContainer = styled.div`
  padding: ${spaceToPx('normal')}px 0 ${spaceToPx('normal')}px 14px;
  width: calc(100% - 290px);
`;

const TemplatesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  overflow: auto;
  // we use vmin here to keep consistency with the modal size.
  max-height: calc(100vh - 10vmin);
`;

const TemplateListRow = styled.div`
  width: 100%;

  &:not(:first-of-type) {
    margin-top: ${spaceToPx('xl')}px;
  }
`;

const SearchIconWrapper = styled(SearchIcon)`
  margin-left: 9px;
  position: absolute;
  top: 9px;
  z-index: 10;
`;

const InputContainer = styled.div`
  display: flex;
  flex: 1;
  margin-bottom: ${spaceToPx('s')}px;
  margin-top: ${spaceToPx('normal')}px;
  min-width: 210px;
  justify-content: space-between;
  position: relative;

  > div {
    width: initial;
  }

  input {
    padding-left: 40px;
  }
`;

const CategoryButton = styled.button<{ active: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  display: block;
  padding: ${spaceToPx('xs')}px 0;
  font-weight: ${getFontWeight('bold')};
  color: ${({ active }: { active: boolean }) =>
    active ? color(BrandPurple) : color(OnyxGray, Lighter3)};
  position: relative;

  &:focus,
  &:hover {
    background: transparent;
    color: ${color(BrandPurple)};
    outline: none;
  }
`;

const TemplateCardsContainer = styled.div<{ showAll: boolean }>`
  display: flex;
  flex-wrap: wrap;
  height: ${({ showAll }: { showAll: boolean }) => (showAll ? 'initial' : '275px')};
  overflow: hidden;
`;

const NewIndicator = styled.span`
  font-size: ${getFontSize('12')};
  line-height: 1;
  padding: 3px 6px;
  margin-left: ${spaceToPx('xs')}px;
  background: ${gradient(AquaTeal)};
  border-radius: ${radius('small')};
  color: ${color(White)};
`;

const getCategoriesList = (categories: string[], selectedCategory: string) => {
  if (selectedCategory === 'All') {
    return categories.filter((category: string) => category !== 'All');
  }

  return [selectedCategory];
};

export const filterTemplates = (
  template: TemplateMetadata,
  filter: string,
  selectedCategory: string,
) => {
  if (template.category === HIDDEN_TEMPLATES_CATEGORY || template.scope === 'Block') {
    return false;
  }

  const matchSearch =
    template.name.toLowerCase().includes(filter.toLowerCase()) ||
    template?.description?.toLowerCase().includes(filter.toLowerCase()) ||
    template?.tags?.some((tag: string) => tag.toLowerCase().includes(filter.toLowerCase()));

  let categoryMatch = false;

  if (selectedCategory === 'All') {
    categoryMatch = true;
  }

  if (template.category === selectedCategory) {
    categoryMatch = true;
  }

  if (selectedCategory === 'Saved Templates' && template.scope === 'Organization') {
    categoryMatch = true;
  }

  return matchSearch && categoryMatch;
};

interface Props {
  resource: TemplateMetadata[];
  documentId: number;
}

export const TemplateList = ({ resource, documentId }: Props) => {
  const [selectedCategory, setCategory] = useState('All');
  const [filter, setFilter] = useState('');
  const dispatch = useDispatch();
  const setTab = (newTab?: string) => dispatch(setPaletteTab(newTab));

  const [selectedTemplateId, selectTemplate] = useState<number>();

  const filteredData = resource.filter((template: TemplateMetadata) =>
    filterTemplates(template as TemplateMetadata, filter as string, selectedCategory as string),
  );

  const uniqueCategories = [
    ...new Set(resource.map(({ category, scope }) => scope !== 'Block' && category)),
  ]
    .filter((category: string) => category && category !== HIDDEN_TEMPLATES_CATEGORY)
    .sort();

  const categories = ['All', ...uniqueCategories, 'Saved Templates'] as string[];

  return (
    <div css={{ display: 'flex' }}>
      <FiltersContainer>
        <Heading
          element="h3"
          size="XS"
          strong
          css={{
            color: `${color(OnyxGray)}`,
            marginBottom: `${spaceToPx('normal')}px`,
          }}
        >
          Templates
        </Heading>
        <InputContainer>
          <Input
            onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
              selectTemplate(undefined);
              setFilter(value);
            }}
            placeholder="Search Template"
          />
          <SearchIconWrapper width="22" />
        </InputContainer>
        <div css={{ overflow: 'auto' }}>
          {categories.map((category: string) => (
            <CategoryButton
              key={category}
              onClick={() => {
                selectTemplate(undefined);
                setCategory(category);
              }}
              active={category === selectedCategory}
            >
              {getCategoryName(category)}
              {filteredData
                .filter((template: TemplateMetadata) => template.category === category)
                .map(
                  (template: TemplateMetadata) => !!template.tags?.some((tag) => /new/i.test(tag)),
                )
                .find((isNew: boolean) => isNew) && <NewIndicator>New templates</NewIndicator>}
            </CategoryButton>
          ))}
        </div>
      </FiltersContainer>

      <TemplatesContainer>
        {!selectedTemplateId && (
          <TemplatesList>
            {getCategoriesList(categories, selectedCategory).map((category: string) => {
              const categoryTemplates = filteredData.filter((template: TemplateMetadata) =>
                category === 'Saved Templates'
                  ? template.scope === 'Organization'
                  : template.category === category,
              );

              if (!categoryTemplates.length && category !== selectedCategory) {
                return null;
              }

              return (
                <TemplateListRow key={category}>
                  <div
                    css={{ display: 'flex', justifyContent: 'space-between', marginRight: '27px' }}
                  >
                    <Heading
                      element="h3"
                      size="XS"
                      strong
                      css={{
                        color: `${color(OnyxGray)}`,
                        marginBottom: `${spaceToPx('normal')}px`,
                      }}
                    >
                      {getCategoryName(category)}
                    </Heading>
                    {selectedCategory === 'All' && !filter && (
                      <CategoryButton active onClick={() => setCategory(category)}>
                        See All
                      </CategoryButton>
                    )}
                  </div>
                  <TemplateCardsContainer showAll={!!filter || selectedCategory !== 'All'}>
                    {categoryTemplates.map((template: TemplateMetadata) => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        onClick={selectTemplate}
                        isNew={!!template.tags?.some((tag) => /new/i.test(tag))}
                      />
                    ))}
                  </TemplateCardsContainer>
                </TemplateListRow>
              );
            })}
          </TemplatesList>
        )}

        {selectedTemplateId && (
          <TemplatePreview
            documentId={documentId}
            toggle={() => setTab()}
            onClickBack={() => selectTemplate(undefined)}
            onSelectTemplate={selectTemplate}
            templateId={selectedTemplateId}
          />
        )}
      </TemplatesContainer>
    </div>
  );
};

export default connectWithHammock({
  endpoint: ENDPOINTS.templateMetadata,
})(TemplateList);
